/**
 * Compact per-game visual identity: a fanned-cards glyph tinted with the
 * game's registry-defined accent color. Used in the home-page game tiles,
 * the lobby header, and the lobby-row chip.
 *
 * Pure SVG so it scales crisply, doesn't need image assets, and is themable
 * just by changing the registry's `accent`.
 */

import type { GameKind } from "../../../shared/protocol.js";
import { gameByKind } from "../games/registry.js";

export interface GameKindBadgeProps {
  kind: GameKind;
  /** Square edge length in px. Default 32. */
  size?: number;
}

export function GameKindBadge({ kind, size = 32 }: GameKindBadgeProps) {
  const reg = gameByKind(kind);
  const accent = reg?.accent ?? "#888";
  const initial = (reg?.label ?? "?").charAt(0).toUpperCase();
  return (
    <span
      className="game-kind-badge"
      style={{
        width: size,
        height: size,
        // Drive the accent in CSS so hover etc. can reuse it.
        ["--game-accent" as string]: accent,
      }}
      aria-hidden="true"
    >
      <svg viewBox="0 0 32 32" width={size} height={size}>
        {/* Two fanned cards behind, one centered in front. The front card
            shows the game's leading initial in its accent color. */}
        <g transform="translate(6 8) rotate(-14 5 8)">
          <rect x="0" y="0" width="14" height="18" rx="2" fill="#1a2b30" stroke={accent} strokeOpacity="0.6" strokeWidth="0.8" />
        </g>
        <g transform="translate(12 8) rotate(14 5 8)">
          <rect x="0" y="0" width="14" height="18" rx="2" fill="#1a2b30" stroke={accent} strokeOpacity="0.6" strokeWidth="0.8" />
        </g>
        <g transform="translate(9 6)">
          <rect x="0" y="0" width="14" height="18" rx="2" fill={accent} stroke="#0a1518" strokeWidth="0.6" />
          <text
            x="7"
            y="13"
            textAnchor="middle"
            fontFamily="'Cinzel', 'Georgia', serif"
            fontSize="11"
            fontWeight="700"
            fill="#0a1518"
          >
            {initial}
          </text>
        </g>
      </svg>
    </span>
  );
}
